/**
 * Combobox - Generic, data-agnostic searchable select built on the shadcn
 * Command + Popover scaffold. Extracted so client-scoped selectors (categories,
 * and future filters) can reuse the search UX without duplicating the scaffold.
 *
 * Supports a pinned "all" sentinel row and an inline clear affordance, both of
 * which resolve the selection to `undefined`.
 */
import { useState } from 'react';
import type { ReactNode, MouseEvent } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/client/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/client/components/ui/popover';

export interface ComboboxProps<T> {
  /** Items to render as options */
  items: T[];
  /** Currently selected key, or undefined for none */
  value: string | undefined;
  /** Called with the new key, or undefined when cleared / all selected */
  onChange: (value: string | undefined) => void;
  /** Stable key for an item (also used for equality against `value`) */
  getKey: (item: T) => string;
  /** Trigger text and search-match text for an item */
  getLabel: (item: T) => string;
  /** Optional custom row rendering (defaults to getLabel) */
  renderItem?: (item: T) => ReactNode;
  /** Trigger placeholder when nothing is selected */
  placeholder?: string;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Text shown when the search yields no matches */
  emptyText?: string;
  /** Whether the control is disabled */
  disabled?: boolean;
  /** Render an inline clear (X) on the trigger when a value is selected */
  clearable?: boolean;
  /** Pinned first row that resolves selection to undefined */
  allOption?: { label: string };
  /** Additional class for the container */
  className?: string;
}

export function Combobox<T>({
  items,
  value,
  onChange,
  getKey,
  getLabel,
  renderItem,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  disabled = false,
  clearable = false,
  allOption,
  className,
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);

  // Treat both undefined and '' (empty search param) as "no selection"
  const hasValue = value !== undefined && value !== '';
  const selectedItem = hasValue ? items.find((item) => getKey(item) === value) : undefined;
  const triggerLabel = selectedItem ? getLabel(selectedItem) : (allOption?.label ?? placeholder);
  const showClear = clearable && hasValue && !disabled;

  const handleSelect = (next: string | undefined) => {
    onChange(next === value ? undefined : next);
    setOpen(false);
  };

  // Clear is a sibling of the trigger (not a descendant), so its click never
  // reaches the Popover trigger and cannot reopen the dropdown.
  const handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn('h-10 w-full justify-between font-normal overflow-hidden', showClear && 'pr-9')}
            >
              <span className="truncate">{triggerLabel}</span>
              {!showClear && <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
            </Button>
          </PopoverTrigger>
          {showClear && (
            <button
              type="button"
              aria-label="Clear selection"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm opacity-60 hover:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[250px] p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {allOption && (
                  <CommandItem
                    value={allOption.label}
                    onSelect={() => handleSelect(undefined)}
                    className={cn(!hasValue && 'bg-accent')}
                  >
                    <Check className={cn('mr-2 h-4 w-4', !hasValue ? 'opacity-100' : 'opacity-0')} />
                    {allOption.label}
                  </CommandItem>
                )}
                {items.map((item) => {
                  const key = getKey(item);
                  return (
                    <CommandItem
                      key={key}
                      value={`${getLabel(item)} ${key}`}
                      onSelect={() => handleSelect(key)}
                      className={cn(value === key && 'bg-accent')}
                    >
                      <Check className={cn('mr-2 h-4 w-4', value === key ? 'opacity-100' : 'opacity-0')} />
                      {renderItem ? renderItem(item) : getLabel(item)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
